<template>
  <div :class="['fixed z-[26] flex flex-col items-end gap-3 transition-[bottom] duration-300 ease-[ease] motion-reduce:transition-none', fabPositionClass]">
    <!-- ツールチップ -->
    <Transition
      enter-active-class="transition duration-300 ease-out motion-reduce:transition-none"
      enter-from-class="opacity-0 translate-y-2 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition duration-200 ease-in motion-reduce:transition-none"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-1 scale-95"
    >
      <ChatFABTooltip v-if="isTooltipVisible" @dismiss="dismissTooltip" @open-chat="handleOpen" />
    </Transition>

    <!-- FAB ピル本体 -->
    <Transition
      enter-active-class="transition duration-[400ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] motion-reduce:duration-200 motion-reduce:ease-in-out motion-reduce:transition-none"
      enter-from-class="opacity-0 scale-[0.8]"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-200 ease-in-out motion-reduce:transition-none"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-[0.8]"
      appear
    >
      <ChatFABPill v-if="!isOpen" :collapsed="isMobileCollapsed" @click="handleOpen" />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { useTimeoutFn } from '@vueuse/core';

import ChatFABPill from '@/wabunka-src/features/Chat/components/model/pure/ChatFABPill/ChatFABPill.vue';
import ChatFABTooltip from '@/wabunka-src/features/Chat/components/model/pure/ChatFABTooltip/ChatFABTooltip.vue';
import { useChatPanel } from '@/wabunka-src/features/Chat/composables/general/useChatPanel';
import { useChatTooltipState } from '@/wabunka-src/features/Chat/composables/general/useChatTooltipState';
import { sendGAAiChatOpenEvent } from '@/wabunka-src/features/Chat/utils/googleAnalytics/chatEvents';

// SPのみ: マウントから何秒経過したらピルラベルを隠してアイコンのみにするか
const PILL_COLLAPSE_DELAY_MS = 5_000;

interface Props {
  slug: string;
  isCtaBarVisible?: boolean;
  hasOverviewBeenSeen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isCtaBarVisible: false,
  hasOverviewBeenSeen: false,
});

const { isOpen, isDesktop, open } = useChatPanel();

const { isVisible: isTooltipVisible, dismiss: dismissTooltip } = useChatTooltipState({
  isOpen,
  hasOverviewBeenSeen: () => props.hasOverviewBeenSeen,
});

// SPのみ: マウントから一定時間経過後にピルラベルを隠して、アイコンのみにして占有面積を抑える
const hasCollapseDelayElapsed = ref(false);
useTimeoutFn(() => {
  hasCollapseDelayElapsed.value = true;
}, PILL_COLLAPSE_DELAY_MS);

const isMobileCollapsed = computed(() => !isDesktop.value && hasCollapseDelayElapsed.value);

const handleOpen = () => {
  sendGAAiChatOpenEvent(props.slug);
  open();
  // チャットを開いた時点でツールチップは役目終了
  dismissTooltip();
};

const fabPositionClass = computed(() => {
  if (props.isCtaBarVisible) return isDesktop.value ? 'bottom-20 right-6' : 'bottom-20 right-4';
  return isDesktop.value ? 'bottom-6 right-6' : 'bottom-6 right-4';
});
</script>
